import { HttpErrorResponse, type HttpEvent, type HttpHandler, type HttpInterceptor, type HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, type Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthService } from '../authentication/_services/auth.service';
import { CustomToastrService } from '../shared/services/custom-toastr.service';
import { ServerMessageService } from '../shared/services/server-message.service';

@Injectable()
export class CentralizedErrorInterceptor implements HttpInterceptor {
  private serverMessageService = inject(ServerMessageService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private customToastr = inject(CustomToastrService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // IGNORE 304 — NOT AN ERROR
        if (error.status === 304) {
          return throwError(() => error);
        }

        let errorMessage = 'Unknown error occurred!';

        if (error.error instanceof ErrorEvent) {
          this.customToastr.error(error.error.message);
          errorMessage = `Error: ${error.error.message}`;
        } else {
          if (error.status === 401) {
            // Only the public label reads (loaded before login) are swallowed. The translation-management
            // calls need a login (ApplicationSupport), so their 401s go through the token refresh like any other.
            if (request.url.includes('/api/i18n/translations') || request.url.includes('/api/i18n/manifest')) {
              return EMPTY;
            }

            if (this.isCloudflareUnauthorized(error)) {
              this.showCloudflareUnauthorizedError();
              return throwError(() => error);
            }

            if (this.isPublicAuthRequest(request.url)) {
              if (this.shouldToastPublicAuth401(request.url)) {
                this.handleAuthFlow401(error);
              }
              return throwError(() => error);
            }

            return this.handle401Errors(request, next);
          }

          this.handleNon401Errors(request, error);
          errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }

        console.error(errorMessage);
        return throwError(() => error);
      })
    );
  }

  private handleNon401Errors(request: HttpRequest<any>, error: HttpErrorResponse): void {
    const isFromBackend = this.isBackendRequest(request.url);

    if (error.status === 0) {
      this.showError('Servers are down. Please contact support for assistance.', 'Network Error');
      if (isFromBackend) {
        this.handleLogout(1);
      }
      return;
    }

    if (error.status === 502) {
      this.showError('The server is temporarily unavailable. Please try again in a few minutes.', 'Service Error');
      if (isFromBackend) {
        this.handleLogout(2);
      }
      return;
    }

    if (error.status === 403) {
      this.showError(this.translate.instant('COMMON.FORBIDDEN_MESSAGE'), 'COMMON.FORBIDDEN');
      return;
    }

    let serverError = error.error;

    if (typeof serverError === 'string') {
      try {
        serverError = JSON.parse(serverError);
      } catch {
        serverError = {};
      }
    }
    const message = this.serverMessageService.getServerMessage(serverError?.messageCode);
    message.subscribe(msg => {
      switch (error.status) {
        case 404:
          this.showError(msg, 'COMMON.NOT_FOUND');
          break;
        case 500:
          this.showError(msg, 'COMMON.SERVER_ERROR');
          break;
        default:
          this.showError(msg, 'COMMON.ERROR');
      }
    });
  }

  private handle401Errors(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.warn('401 Unauthorized detected, attempting token refresh');

    if (req.url.includes('/REFRESHACCESSTOKEN')) {
      console.error('Refresh token request itself returned 401 - user is logged out on server');
      this.showError(this.translate.instant('COMMON.UNAUTHORIZED_MESSAGE'), 'COMMON.UNAUTHORIZED');
      this.handleLogout(3);
      return throwError(() => new Error('Refresh token is invalid or expired'));
    }

    if (req.headers.has('x-retry')) {
      console.warn('Second 401 after retry → forcing logout');
      this.showError(this.translate.instant('COMMON.UNAUTHORIZED_MESSAGE'), 'COMMON.UNAUTHORIZED');
      this.handleLogout(4);
      return throwError(() => new Error('Unauthorized even after retry'));
    }

    const staleToken = req.headers.get('Authorization') ?? localStorage.getItem(this.authService.tokenKey);
    if (!staleToken) {
      this.showError(this.translate.instant('COMMON.UNAUTHORIZED_MESSAGE'), 'COMMON.UNAUTHORIZED');
      console.warn('401 occurred but no access token exists → user is truly unauthorized');
      this.handleLogout(5);
      return throwError(() => new Error('Unauthorized access'));
    }

    return this.authService.refreshToken({ forceRefresh: true, staleAccessToken: staleToken }).pipe(
      switchMap((): Observable<HttpEvent<any>> => {
        console.warn('Token refresh succeeded');
        const newToken = localStorage.getItem(this.authService.tokenKey);
        if (!newToken) {
          throw new Error('No token after refresh');
        }
        console.warn('Retrying request with new token:', req.url);
        return next.handle(this.addAuthHeader(req.clone({ setHeaders: { 'x-retry': '1' } }), newToken));
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status !== 401 && err.status !== 403) {
          this.handleNon401Errors(req, err);
          return throwError(() => err);
        }
        console.error('Token refresh failed — logging out', err);
        this.showError(this.translate.instant('COMMON.UNAUTHORIZED_MESSAGE'), 'COMMON.UNAUTHORIZED');
        this.handleLogout(6);
        return throwError(() => err);
      })
    );
  }

  private isPublicAuthRequest(url: string): boolean {
    return this.matchesPublicAuthPath(url, /\/(login|requestOtp|forgotPassword|replaceForgottenPassword)$/);
  }

  private shouldToastPublicAuth401(url: string): boolean {
    return this.matchesPublicAuthPath(url, /\/(login|requestOtp)$/);
  }

  private matchesPublicAuthPath(url: string, pattern: RegExp): boolean {
    try {
      return pattern.test(new URL(url, 'http://localhost').pathname);
    } catch {
      return pattern.test(url.split('?')[0]);
    }
  }

  private isCloudflareUnauthorized(error: HttpErrorResponse): boolean {
    const server = error.headers?.get('server') ?? '';
    const body = typeof error.error === 'string' ? error.error : '';
    const url = error.url ?? '';

    return (
      !!error.headers?.get('cf-ray') ||
      !!error.headers?.get('cf-mitigated') ||
      /cloudflare/i.test(server) ||
      /cloudflare/i.test(body) ||
      url.includes('challenges.cloudflare.com') ||
      url.includes('cdn-cgi/challenge')
    );
  }

  private showCloudflareUnauthorizedError(): void {
    const translated = this.translate.instant('LOGIN.TURNSTILE_ERROR');
    const message = translated === 'LOGIN.TURNSTILE_ERROR' ? 'Security verification failed. Please try again.' : translated;
    this.showError(message, 'COMMON.ERROR');
  }

  private handleAuthFlow401(error: HttpErrorResponse): void {
    let serverError = error.error;
    if (typeof serverError === 'string') {
      try {
        serverError = JSON.parse(serverError);
      } catch {
        serverError = {};
      }
    }

    const messageCode = serverError?.messageCode;
    if (messageCode) {
      this.serverMessageService.getServerMessage(messageCode).subscribe(msg => {
        this.showError(msg || this.translate.instant('COMMON.UNAUTHORIZED_MESSAGE'), 'COMMON.UNAUTHORIZED');
      });
      return;
    }

    this.showError(this.translate.instant('COMMON.UNAUTHORIZED_MESSAGE'), 'COMMON.UNAUTHORIZED');
  }

  private addAuthHeader(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (!token) {
      return req;
    }
    const cleanToken = token.startsWith('Bearer ') ? token.substring('Bearer '.length).trim() : token;
    return req.clone({ setHeaders: { Authorization: `Bearer ${cleanToken}` } });
  }

  private isBackendRequest(url: string): boolean {
    return [environment.serverApp].some(server => url.includes(server));
  }

  private showError(message: string, title: string): void {
    this.customToastr.error(message, this.translate.instant(title));
  }

  private handleLogout(source: number): void {
    console.warn(`(${source}) Initiating logout sequence`);
    this.authService.logout();
  }
}
